import { Link, useNavigate } from 'react-router-dom';
import { workQueueApi } from '@/api/workQueue.api';
import { Button } from '@/components/ui/Button';
import { WorkQueuePriorityBadge } from './WorkQueuePriorityBadge';
import type { WorkQueueItem } from '@/types/workQueue';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

interface WorkQueueItemCardProps {
  item: WorkQueueItem;
  onActionComplete?: () => void;
}

export function WorkQueueItemCard({ item, onActionComplete }: WorkQueueItemCardProps) {
  const navigate = useNavigate();

  const runAction = async (action: string) => {
    if (action === 'OPEN') {
      navigate(item.deepLink);
      return;
    }
    try {
      await workQueueApi.executeAction(item.sourceType, item.sourceId, action);
      notify.success(action === 'APPROVE' ? 'Approved' : action === 'REJECT' ? 'Rejected' : 'Done');
      onActionComplete?.();
    } catch (err) {
      showApiError(err, `${action} failed`);
    }
  };

  return (
    <article className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <WorkQueuePriorityBadge priority={item.priority} />
          <span className="text-xs font-medium uppercase text-slate-500">{item.module}</span>
          <span className="text-xs text-slate-400">{item.workType.replace(/_/g, ' ')}</span>
        </div>
        <h3 className="font-semibold text-slate-900 dark:text-slate-100">{item.title}</h3>
        {item.description && (
          <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">{item.description}</p>
        )}
        <p className="mt-2 text-xs text-slate-500">
          {item.status}
          {item.dueAt && ` · Due ${new Date(item.dueAt).toLocaleString()}`}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {(item.quickActions ?? ['OPEN']).map((action) => (
          <Button
            key={action}
            size="sm"
            variant="secondary"
            onClick={() => void runAction(action)}
          >
            {action.charAt(0) + action.slice(1).toLowerCase()}
          </Button>
        ))}
        <Link to={item.deepLink} className="btn btn-secondary btn-sm">
          Open
        </Link>
      </div>
    </article>
  );
}
